import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button.js';
import infoIcon from '../assets/info-icon.svg';

/**
 * Page-view permission-denied surface (issue #248, block-message UX).
 *
 * Screen routes are gated by `canViewScreen` (deny-by-default RBAC). Previously a
 * denied page silently redirected the agent home (`<Navigate to="/" replace />`),
 * which gave no feedback that access was refused. Per @dylan219's authoritative
 * block-message copy (2026-07-03), a page the agent is not permitted to view now
 * renders this panel with the VERBATIM message and a Back button that returns
 * them to the last page they were on.
 *
 * This is the PAGE-VIEW denial surface only. The separate inline
 * "Sorry, you do not have permission to perform this action." affordance beside a
 * blocked action is a different increment and is NOT built here.
 *
 * The authoritative enforcement remains RBAC (`canViewScreen`) + the server-side
 * route guards; this is a UX affordance shown when that gate refuses the view.
 */
export function PermissionDeniedPage(): React.JSX.Element {
  const navigate = useNavigate();

  const goBack = (): void => {
    // Return the agent to the page they came from. If there is no history to go
    // back to (deep-link / first entry in this session) `navigate(-1)` would be a
    // dead end, so fall back to the app root.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <main
      className="max-w-[900px] mx-auto px-12 py-12"
      aria-labelledby="permission-denied-heading"
    >
      <div className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3">
        <img
          src={infoIcon}
          alt=""
          aria-hidden="true"
          className="w-5 h-5 mt-1 shrink-0"
        />
        <div>
          <h1
            id="permission-denied-heading"
            role="alert"
            className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-4"
          >
            You do not have permission to view this page
          </h1>
          <Button
            type="button"
            onClick={goBack}
            aria-label="Back"
            className="bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E] hover:bg-[#e55a2a] hover:border-[#e55a2a] rounded-md px-6 py-3 h-auto font-['Arial',sans-serif] font-bold text-[16px]"
          >
            Back
          </Button>
        </div>
      </div>
    </main>
  );
}
